import type { TGooglePlaceSuggestion } from "@/api/google-maps/search/google-search.type.ts";
import type { IAddressAdapter } from "@/domains/address/adapters/address-adapter.ts";
import type { TGoogleNormalizedAddress } from "@/domains/address/dto/normalized-address.ts";
import type { TGeocodedAddressDto } from "@/types/backoffice/dto/site/site-dto.ts";

import { EAddressProvider } from "@/domains/address/providers/address-provider.types.ts";
import { ECountryCode } from "@/domains/country/country.types.ts";
import { EGoogleAddressType } from "@/lib/google.ts";

type TAddressComponent = TGooglePlaceSuggestion["addressComponents"][number];

function findStreetNumber(addressComponent: TAddressComponent) {
  if (addressComponent.types.includes(EGoogleAddressType.STREET_NUMBER)) {
    return addressComponent.longText;
  }
  return null;
}

function findStreet(addressComponent: TAddressComponent) {
  if (addressComponent.types.includes(EGoogleAddressType.ROUTE)) {
    return addressComponent.longText;
  }
  if (addressComponent.types.includes(EGoogleAddressType.PREMISE)) {
    return addressComponent.longText;
  }
  return null;
}

function findCity(addressComponent: TAddressComponent) {
  if (addressComponent.types.includes(EGoogleAddressType.POSTAL_TOWN)) {
    return addressComponent.longText;
  }
  if (addressComponent.types.includes(EGoogleAddressType.LOCALITY)) {
    return addressComponent.longText;
  }
  if (
    addressComponent.types.includes(EGoogleAddressType.SUBLOCALITY) &&
    addressComponent.types.includes(EGoogleAddressType.SUBLOCALITY_LEVEL_1)
  ) {
    return addressComponent.longText;
  }

  return null;
}

function findAdministrativeDivision(addressComponent: TAddressComponent) {
  if (addressComponent.types.includes(EGoogleAddressType.ADMINISTRATIVE_AREA_LEVEL_2) && addressComponent.longText) {
    return addressComponent.longText;
  }
  if (addressComponent.types.includes(EGoogleAddressType.ADMINISTRATIVE_AREA_LEVEL_1) && addressComponent.longText) {
    return addressComponent.longText;
  }
  return null;
}

function findCountry(addressComponent: TAddressComponent) {
  if (
    addressComponent.types.includes(EGoogleAddressType.COUNTRY) &&
    addressComponent.longText &&
    addressComponent.shortText
  ) {
    return { label: addressComponent.longText, isoCode: addressComponent.shortText };
  }
  return null;
}

function findZipCode(addressComponent: TAddressComponent) {
  if (addressComponent.types.includes(EGoogleAddressType.POSTAL_CODE)) {
    return addressComponent.longText;
  }
  return null;
}

export const GoogleNormalizedAddressToBackofficeAddressAdapter: IAddressAdapter<
  TGoogleNormalizedAddress,
  TGeocodedAddressDto
> = {
  adapt(address: TGoogleNormalizedAddress): TGeocodedAddressDto {
    const geocodedAddress: TGeocodedAddressDto = {
      externId: address.raw.id,
      geocoding: {
        latitude: address.raw.location.latitude,
        longitude: address.raw.location.longitude,
      },
      zipCode: null,
      city: null,
      cityCode: null, // insee code not relevant with google search
      administrativeDivision: [],
      country: null,
      inseeCode: null, // insee code not relevant with google search
      provider: EAddressProvider.GOOGLE,
      score: null, // No score associated to google search results
      label: address.raw.formattedAddress ?? "",
    };

    address.raw.addressComponents.forEach((addressComponent) => {
      const foundStreetNumber = findStreetNumber(addressComponent);
      if (foundStreetNumber) {
        geocodedAddress.streetNumber = foundStreetNumber;
      }

      const foundStreet = findStreet(addressComponent);
      if (foundStreet) {
        geocodedAddress.street = foundStreet;
      }

      const foundCity = findCity(addressComponent);
      if (foundCity) {
        geocodedAddress.city = foundCity;
      }

      const foundAdministrativeDivision = findAdministrativeDivision(addressComponent);
      if (foundAdministrativeDivision) {
        geocodedAddress.administrativeDivision = [
          ...geocodedAddress.administrativeDivision,
          foundAdministrativeDivision,
        ];
      }

      const foundCountry = findCountry(addressComponent);
      if (foundCountry) {
        geocodedAddress.country = foundCountry;
      }

      const foundZipCode = findZipCode(addressComponent);
      if (foundZipCode) {
        geocodedAddress.zipCode = foundZipCode;
      }
    });

    // En Hongrie il n'y a pas de division administrative au dessus des villes mais des régions statistiques
    if (
      geocodedAddress.administrativeDivision.length === 0 &&
      geocodedAddress.country?.isoCode === ECountryCode.HUNGARY
    ) {
      geocodedAddress.administrativeDivision = [geocodedAddress.country?.label];
    }

    return geocodedAddress;
  },
};
